import axios from 'axios'

import { DAYS_OF_WEEK } from 'resources'
import { stadiumApi, porticoApi } from './axios'
import { decamelizeKeys, camelizeKeys } from 'humps'
import { get } from 'lodash'

export const uploadImageToS3 = (data) => {
  return stadiumApi.post('/external/images/upload_image', data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

export const createItemMapping = (data) => {
  let params = { data: decamelizeKeys(data) }
  return stadiumApi.post('/operator/item_mappings', params)
}

export const updateItemMapping = (data) => {
  let params = { data: decamelizeKeys(data) }
  return stadiumApi.put('/operator/item_mappings', params)
}

export const fetchItemMappings = (venueUuid) =>
  stadiumApi.get('/operator/item_mappings', {
    params: { venue_uuid: venueUuid },
  })

export const deleteItemMapping = (uuid) =>
  stadiumApi.delete(`/operator/item_mappings/${uuid}`)

export const fetchMerchants = (
  venueUuid,
  per,
  page,
  searchFor,
  searchBy
) =>
  stadiumApi.get('/operator/merchants', {
    params: {
      venue_uuid: venueUuid ?? null,
      per: per ?? null,
      page: page ?? null,
      search_for: searchFor ?? null,
      search_by: searchBy ?? null,
    },
  })

export const createMerchant = (data) => {
  let params = { data: decamelizeKeys(data) }
  return stadiumApi.post('operator/merchants', params)
}

export const updateMerchant = (data) => {
  let params = { data: decamelizeKeys(data) }
  return stadiumApi.put('operator/merchants', params)
}

export const deleteMerchant = (uuid) =>
  stadiumApi.delete(`operator/merchants/${uuid}`)

export const fetchCustomPaymentTypes = (params) => {
  const { venueUuid, per, page, searchBy, searchFor } = params

  return stadiumApi.get('/operator/custom_payment_types', {
    params: {
      venue_uuid: venueUuid,
      per,
      page,
      search_for: searchFor,
      search_by: searchBy,
    },
  })
}

export const createCustomPaymentType = (data) => {
  let params = { data: decamelizeKeys(data) }
  return stadiumApi.post('operator/custom_payment_types', params)
}

export const updateCustomPaymentType = (data) => {
  let params = { data: decamelizeKeys(data) }
  return stadiumApi.put('operator/custom_payment_types', params)
}

export const deleteCustomPaymentType = (uuid) =>
  stadiumApi.delete(`operator/custom_payment_types/${uuid}`)

export const fetchSalesEvents = (
  venueUuid,
  per,
  page,
  searchFor,
  searchBy
) =>
  stadiumApi.get('operator/sales_events', {
    params: {
      venue_uuid: venueUuid ?? null,
      per: per ?? null,
      page: page ?? null,
      search_for: searchFor ?? null,
      search_by: searchBy ?? null,
    },
  })

export const createSalesEvent = (data) => {
  let params = { data: decamelizeKeys(data) }
  return stadiumApi.post('operator/sales_events', params)
}

export const updateSalesEvent = (data) => {
  let params = { data: decamelizeKeys(data) }
  return stadiumApi.put('operator/sales_events', params)
}

export const fetchAvailability = (venueUuid) =>
  stadiumApi.get(`/mobile_web/v1/availability/venues/${venueUuid}`)

export const fetchPriceLevels = (venueUuid) =>
  stadiumApi.get('/operator/item_property_overrides', {
    params: { venue_uuid: venueUuid },
  })

export const createSettings = (data) => {
  let params = decamelizeKeys({ data })

  params.data = { settings: [...params.data] }
  return stadiumApi.post('/external/settings', params)
}

export const fetchSettings = () => {
  return stadiumApi.get('/external/settings')
}

export const fetchSettingsWithDefaultSettings = (
  venueUuid,
  includeDefaultSettings = true
) => {
  return stadiumApi.get('/operator/settings', {
    params: {
      include_default_settings: includeDefaultSettings,
      venue_uuid: venueUuid,
    },
  })
}

export const createSettingsNew = (data) => {
  let params = decamelizeKeys({ data })

  params.data = { settings: [...params.data] }
  return stadiumApi.post('/external/settings', params)
}

export const updatePriceLevel = (data) => {
  let params = { data: decamelizeKeys(data) }
  return stadiumApi.put('/operator/item_property_overrides', params)
}

export const createPriceLevel = (data) => {
  let params = { data: decamelizeKeys(data) }
  return stadiumApi.post('/operator/item_property_overrides', params)
}

export const fetchSalesEventTypes = (venueUuid) =>
  stadiumApi.get('/operator/sales_event_types', {
    params: { venue_uuid: venueUuid },
  })

export const fetchVenues = () => porticoApi.get(`/devices/venues`)

export const fetchMenus = (venueUuid, includeSettings) =>
  stadiumApi.get('/operator/menus', {
    params: {
      venue_uuid: venueUuid,
      include_settings: includeSettings,
    },
    transformResponse: [
      ...axios.defaults.transformResponse,
      (data) =>
        camelizeKeys(data, (key, convert) => {
          // we have to check if the key is day of the week in order to not convert days started with capital letter, we use it in the
          // Revenue Centers > Menu Settings > Business Hours and Order Ahead Hours
          const isDayOfWeek = DAYS_OF_WEEK.includes(key)
          return isDayOfWeek || /-/.test(key) ? key : convert(key)
        }),
    ],
  })

export const createMenus = (data) => {
  const params = { data: { menus: data } }

  return stadiumApi.post('/operator/menus', params, {
    transformRequest: [
      (data) =>
        decamelizeKeys(get(data, 'data'), (key, convert) => {
          // we have to check if the key is day of the week in order to not convert days started with capital letter, we use it in the
          // Revenue Centers > Menu Settings > Business Hours and Order Ahead Hours
          const isDayOfWeek = DAYS_OF_WEEK.includes(key)
          return isDayOfWeek || /-/.test(key) ? key : convert(key)
        }),
      ...axios.defaults.transformRequest,
    ],
  })
}

export const updateMenus = (data) => {
  const params = { data: { menus: data } }

  return stadiumApi.put('/operator/menus', params, {
    transformRequest: [
      (data) => {
        return decamelizeKeys(get(data, 'data'), (key, convert) => {
          // we have to check if the key is day of the week in order to not convert days started with capital letter, we use it in the
          // Revenue Centers > Menu Settings > Business Hours and Order Ahead Hours
          const isDayOfWeek = DAYS_OF_WEEK.includes(key)
          return isDayOfWeek || /-/.test(key) ? key : convert(key)
        })
      },
      ...axios.defaults.transformRequest,
    ],
  })
}

export const fetchItems = (venueUuid) =>
  stadiumApi.get('/operator/items', {
    params: { venue_uuid: venueUuid },
  })

export const fetchFilteredAndPaginatedItems = ({
  per,
  page,
  searchBy,
  searchFor,
  venueUuid,
}) =>
  stadiumApi.get('/operator/items', {
    params: {
      per,
      page,
      sort_by: 'name',
      search_by: searchBy,
      search_for: searchFor,
      venue_uuid: venueUuid,
    },
  })

export const fetchModifierGroups = (venueUuid) =>
  stadiumApi.get('/operator/item_categories', {
    params: {
      venue_uuid: venueUuid,
      display_type: 'modifier_categories',
    },
  })

export const fetchReportingCategories = (venueUuid) =>
  stadiumApi.get('/operator/item_categories', {
    params: {
      venue_uuid: venueUuid,
      display_type: 'reporting_categories',
    },
  })

export const fetchFilteredAndPaginatedReportingCategories = (
  params
) => {
  const { venueUuid, per, page } = params

  return stadiumApi.get('/operator/item_categories', {
    params: {
      venue_uuid: venueUuid,
      display_type: 'reporting_categories',
      per,
      page,
    },
  })
}

export const updateReportingCategory = (data) => {
  let params = { data: { item_categories: decamelizeKeys(data) } }
  return stadiumApi.put('/operator/item_categories', params)
}

export const createReportingCategory = (data) => {
  let params = { data: { item_categories: decamelizeKeys(data) } }

  return stadiumApi.post('/operator/item_categories', params)
}

export const fetchCategories = (venueUuid) =>
  stadiumApi.get('/operator/item_categories', {
    params: { venue_uuid: venueUuid },
  })

export const createCategory = (data) => {
  let params = { data: decamelizeKeys(data) }
  return stadiumApi.post('/operator/item_categories', params)
}

export const updateCategory = (data) => {
  let params = { data: decamelizeKeys(data) }
  return stadiumApi.put('/operator/item_categories', params)
}

export const fetchDisplayCategories = (params) => {
  const { venueUuid } = params

  return stadiumApi.get('/operator/item_categories', {
    params: {
      venue_uuid: venueUuid,
      display_type: 'display_categories',
    },
  })
}

export const createDisplayCategories = (data) => {
  let params = { data: decamelizeKeys(data) }
  return stadiumApi.post('/operator/item_categories', params)
}

export const updateDisplayCategories = (data) => {
  let params = { data: decamelizeKeys(data) }

  return stadiumApi.put('/operator/item_categories', params)
}

export const fetchRevenueCenters = (venueUuid, includeSettings) =>
  stadiumApi.get('operator/stands', {
    params: {
      venue_uuid: venueUuid,
      include_settings: includeSettings,
    },
  })

export const fetchFilteredAndPaginatedRevenueCenters = (params) => {
  const {
    venueUuid,
    includeSettings,
    searchBy,
    searchFor,
    serviceType,
    per,
    page,
    sortBy,
  } = params

  return stadiumApi.get('operator/stands', {
    params: {
      venue_uuid: venueUuid,
      include_settings: includeSettings,
      search_by: searchBy,
      search_for: searchFor,
      sort_by: sortBy,
      service_type: serviceType,
      per,
      page,
    },
  })
}

export const createRevenueCenters = (data) => {
  const params = { data: { stands: [data] } }

  return stadiumApi.post('/operator/stands', params)
}

export const updateRevenueCenters = (data) => {
  const params = { data: { stands: [data] } }

  return stadiumApi.put('/operator/stands', params)
}

export const createItem = (venueUuid, data) => {
  data.venue_uuid = venueUuid
  let params = decamelizeKeys({ data })

  params.data = { items: [params.data] }

  return stadiumApi.post(
    `/operator/items?venue_uuid=${venueUuid}`,
    params
  )
}

export const createSalesEventType = (data) => {
  const params = {
    data,
  }

  return stadiumApi.post('/operator/sales_event_types', params)
}

export const updateSalesEventType = (data) => {
  const params = {
    data,
  }

  return stadiumApi.put('/operator/sales_event_types', params)
}

export const deleteSalesEventType = (uuid) => {
  return stadiumApi.delete(
    `/operator/sales_event_types/${uuid}`
  )
}

export const removeItem = (uuid) =>
  stadiumApi.delete(`/operator/items/${uuid}`)

export const updateItem = (data) => {
  const params = { data: { items: [...data] } }

  return stadiumApi.put('/operator/items/', params)
}

export const fetchFeatureFlags = (venueUuid, orgName) =>
  stadiumApi.get('/operator/support/settings', {
    params: { venue_uuid: venueUuid, organization_name: orgName },
  })

export const fetchConfigurationModels = () =>
  stadiumApi.get('/operator/configuration_models')

export const fetchFilteredAndPaginatedAttendants = (params) => {
  const {
    orgName,
    per,
    page,
    searchFor,
    searchBy,
    employeeRole,
    active,
    sortBy,
  } = params

  return stadiumApi.get('/operator/attendants', {
    params: {
      organization_name: orgName,
      per,
      page,
      search_for: searchFor,
      search_by: searchBy,
      employee_role: employeeRole,
      active,
      sort_by: sortBy,
    },
  })
}

export const createAttendant = (data) => {
  let params = { data: { attendants: [decamelizeKeys(data)] } }

  return stadiumApi.post('/operator/attendants', params)
}

export const updateAttendant = (data) => {
  let params = { data: { attendants: [decamelizeKeys(data)] } }

  return stadiumApi.put('/operator/attendants', params)
}

export const createEmployeeRole = (data) => {
  let params = { data: { employee_roles: [decamelizeKeys(data)] } }
  return stadiumApi.post('/operator/employee_roles', params)
}

export const deleteEmployeeRole = (uuid) => {
  return stadiumApi.delete(`/operator/employee_roles/${uuid}`)
}

export const getEmployeeRole = (employeeUuid) => {
  const params = {
    employee_uuid: employeeUuid,
  }

  return stadiumApi.get(`/operator/employee_roles`, { params })
}

const api = {
  createItemMapping,
  updateItemMapping,
  deleteItemMapping,
  fetchItemMappings,
  fetchMerchants,
  createMerchant,
  updateMerchant,
  deleteMerchant,
  fetchCustomPaymentTypes,
  createCustomPaymentType,
  updateCustomPaymentType,
  deleteCustomPaymentType,
  fetchSalesEvents,
  createSalesEvent,
  updateSalesEvent,
  fetchSalesEventTypes,
  createSalesEventType,
  updateSalesEventType,
  deleteSalesEventType,
  createPriceLevel,
  fetchPriceLevels,
  updatePriceLevel,
  fetchItems,
  createItem,
  removeItem,
  updateItem,
  fetchCategories,
  createCategory,
  updateCategory,
  fetchMenus,
  createMenus,
  updateMenus,
  fetchRevenueCenters,
  fetchFilteredAndPaginatedRevenueCenters,
  createRevenueCenters,
  updateRevenueCenters,
  fetchVenues,
  updateReportingCategory,
  createReportingCategory,
  createSettings,
  fetchSettings,
  fetchFilteredAndPaginatedItems,
  fetchFeatureFlags,
  fetchConfigurationModels,
  fetchReportingCategories,
  fetchDisplayCategories,
  createDisplayCategories,
  updateDisplayCategories,
  fetchSettingsWithDefaultSettings,
  createSettingsNew,
  fetchModifierGroups,
  fetchFilteredAndPaginatedReportingCategories,
  fetchFilteredAndPaginatedAttendants,
  createAttendant,
  updateAttendant,
  createEmployeeRole,
  deleteEmployeeRole,
  getEmployeeRole,
  uploadImageToS3,
}

export default api
